import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Globe, Languages, Search, Bell, Filter, Users, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";
import LanguageSelector from "@/components/LanguageSelector";

const EU_LANGUAGES = [
  "Български",
  "Čeština",
  "Dansk",
  "Deutsch",
  "Eesti",
  "Ελληνικά",
  "English",
  "Español",
  "Français",
  "Gaeilge",
  "Hrvatski",
  "Italiano",
  "Latviešu",
  "Lietuvių",
  "Magyar",
  "Malti",
  "Nederlands",
  "Polski",
  "Português",
  "Română",
  "Slovenčina",
  "Slovenščina",
  "Suomi",
  "Svenska",
];

export default function AccessoMultilingue() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Accesso Multilingue ai Bandi UE"
        description="Accedi ai bandi pubblici europei in tutte le 24 lingue ufficiali UE. Supera le barriere linguistiche e monitora i bandi TED con TED Monitor."
        path="/it/accesso-multilingue"
        alternates={[
          { lang: "it", href: "https://tedmonitor.eu/it/accesso-multilingue" },
          { lang: "en", href: "https://tedmonitor.eu/multilingual-access" },
          { lang: "x-default", href: "https://tedmonitor.eu/multilingual-access" },
        ]}
      />

      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-mark.png" alt="TED Monitor" className="h-8 w-8" />
            <span className="font-semibold text-lg text-primary">TED Monitor</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <Button asChild>
              <Link to="/auth">Inizia ora</Link>
            </Button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="py-8 md:py-12 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <h1 className="text-3xl md:text-4xl font-bold text-primary mb-4 leading-tight">
              Accesso multilingue ai bandi pubblici europei
            </h1>

            <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-3">
              Il mercato degli appalti UE è intrinsecamente multilingue. I bandi vengono pubblicati in diversi Stati
              membri e lingue, creando potenziali ostacoli per le organizzazioni che operano a livello internazionale.
            </p>

            <p className="text-base font-medium text-foreground">
              TED Monitor fornisce accesso ai bandi UE in tutte le 24 lingue ufficiali, aiutandoti a monitorare
              opportunità oltre confine con meno barriere linguistiche.
            </p>

            {/* Internal links (SEO cluster) */}
            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/it/monitoraggio-bandi-ue"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Monitoraggio bandi UE
              </Link>
              <Link
                to="/it/alert-bandi-ted"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Alert bandi TED
              </Link>
              <Link
                to="/it/software-appalti-pubblici-europei"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Software appalti europei
              </Link>
              <Link
                to="/it/conformita-sicurezza"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Conformità e sicurezza
              </Link>
              <Link
                to="/it/prezzi"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Prezzi
              </Link>
            </div>

            <p className="mt-4 text-[11px] text-muted-foreground">
              Fonte ufficiale: TED (Tenders Electronic Daily) – Supplemento alla Gazzetta Ufficiale dell'Unione Europea.
            </p>
          </div>
        </section>

        {/* 24 lingue */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Languages className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Accedi ai bandi UE in tutte le 24 lingue ufficiali
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              L'Unione Europea riconosce 24 lingue ufficiali, tutte potenzialmente presenti negli avvisi di appalto. Con
              TED Monitor puoi:
            </p>

            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Consultare bandi pubblicati in qualsiasi lingua ufficiale UE",
                "Esaminare informazioni sui bandi senza essere limitato a un singolo ecosistema linguistico",
                "Operare in più paesi UE con un unico workflow centralizzato",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            {/* Language badges */}
            <div className="flex flex-wrap gap-1.5">
              {EU_LANGUAGES.map((lang) => (
                <span
                  key={lang}
                  className="px-2 py-0.5 bg-primary/10 text-primary rounded-full text-[10px] font-medium"
                >
                  {lang}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Superare le barriere */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Globe className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Supera le barriere linguistiche negli appalti pubblici
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              La lingua è uno dei principali ostacoli alla scoperta di appalti transfrontalieri. L'accesso multilingue
              ti aiuta a:
            </p>

            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Identificare opportunità fuori dal tuo mercato domestico",
                "Espandere la tua pipeline di procurement in tutta l'UE",
                "Ridurre il rischio di perdere bandi rilevanti a causa di vincoli linguistici",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-muted-foreground leading-relaxed text-sm">
              Il monitoraggio non dovrebbe essere limitato dalla lingua di pubblicazione dell'avviso.
            </p>

            <p className="mt-4">
              <Link to="/it/monitoraggio-bandi-ue" className="text-primary hover:underline font-medium text-sm">
                Esplora il monitoraggio bandi UE →
              </Link>
            </p>
          </div>
        </section>

        {/* Transfrontaliero */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Building2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Progettato per il procurement transfrontaliero
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Il procurement UE è sempre più transfrontaliero. L'accesso multilingue è un requisito fondamentale per:
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              <div className="p-2 bg-muted/50 rounded-lg border text-center">
                <Users className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Aziende attive in più mercati UE</p>
              </div>
              <div className="p-2 bg-muted/50 rounded-lg border text-center">
                <Globe className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Consulenti internazionali</p>
              </div>
              <div className="p-2 bg-muted/50 rounded-lg border text-center">
                <Building2 className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Studi professionali</p>
              </div>
              <div className="p-2 bg-muted/50 rounded-lg border text-center">
                <Languages className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Operatori in espansione nell'UE</p>
              </div>
            </div>

            <p className="text-muted-foreground leading-relaxed text-sm">
              L'accesso multilingue non è una funzionalità opzionale—è un'infrastruttura procurement essenziale.
            </p>

            <p className="mt-4">
              <Link
                to="/it/software-appalti-pubblici-europei"
                className="text-primary hover:underline font-medium text-sm"
              >
                Esplora il software appalti europei →
              </Link>
            </p>
          </div>
        </section>

        {/* Integrato */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Search className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Accesso multilingue integrato con il monitoraggio bandi
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              L'accesso multilingue è integrato nei workflow di monitoraggio principali, inclusi:
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              <div className="p-2 bg-background rounded-lg border text-center">
                <Search className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Ricerca bandi</p>
              </div>
              <div className="p-2 bg-background rounded-lg border text-center">
                <Filter className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Filtro CPV</p>
              </div>
              <div className="p-2 bg-background rounded-lg border text-center">
                <Bell className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Alert automatici</p>
              </div>
              <div className="p-2 bg-background rounded-lg border text-center">
                <Globe className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Visibilità di mercato</p>
              </div>
            </div>

            <p className="text-muted-foreground leading-relaxed text-sm">
              Questo supporta un monitoraggio coerente indipendentemente dalla lingua di pubblicazione del bando.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-8 md:py-12 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-xl md:text-2xl font-semibold mb-4">Accedi ai bandi UE senza limiti linguistici</h2>
            <p className="text-base mb-6 opacity-90">
              Inizia a monitorare le opportunità di appalto pubblico UE in tutti i paesi e lingue con un'unica
              piattaforma.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="default" variant="secondary" asChild>
                <Link to="/it/prezzi" className="gap-2">
                  Vedi i prezzi
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button
                size="default"
                variant="outline"
                className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                asChild
              >
                <Link to="/auth">Inizia ora</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/logo-mark.png" alt="TED Monitor" className="h-6 w-6" />
              <span className="text-sm text-muted-foreground">© {new Date().getFullYear()} TED Monitor</span>
            </div>
            <nav className="flex flex-wrap gap-6 text-sm">
              <Link to="/it/prezzi" className="text-muted-foreground hover:text-foreground">
                Prezzi
              </Link>
              <Link to="/it/monitoraggio-bandi-ue" className="text-muted-foreground hover:text-foreground">
                Monitoraggio bandi UE
              </Link>
              <Link to="/it/alert-bandi-ted" className="text-muted-foreground hover:text-foreground">
                Alert bandi
              </Link>
              <Link to="/it/software-appalti-pubblici-europei" className="text-muted-foreground hover:text-foreground">
                Software appalti
              </Link>
              <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground">
                Privacy
              </Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
